import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { DraftManual, ManualPermissions } from 'views/App/Portal/Policy/_types/manuals/draft';
import useStickyState from 'hooks/useStickyState';
import useDraftManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftManual';

type DraftManualCRQProviderProps = {
  children: React.ReactNode;
};

export type DraftManualCRQProviderResponse = {
  manualPermissions: ManualPermissions | null;
  setManualPermissions: (manualPermissions: ManualPermissions | null) => void;
  manualHasPermissions: (manualId: string, keys: string[]) => boolean;
  manualId: string;
  manual: DraftManual;
  manualQuery: any;
};

export const DraftManualCRQContext = React.createContext<DraftManualCRQProviderResponse>(
  {} as DraftManualCRQProviderResponse
);

const DraftManualCRQProvider: React.FC<DraftManualCRQProviderProps> = ({ children }) => {
  const { id } = useParams<{ id: string }>();

  const manualQuery = useDraftManual(id, {
    onSuccess: (data: DraftManual) => {
      setManualPermissions((prev) => {
        prev = prev ?? {};
        prev[data.id] = (data.authorizations ?? []).map((x) => x.action).sort();
        return prev;
      });
    },
  });
  const [manualPermissions, setManualPermissions] = useStickyState<ManualPermissions | null>(
    'phx-manualPermissions',
    null
  );

  const manualHasPermissions = useCallback(
    (manualId: string, keys: string[]) => {
      let permissionKeys: string[] = [];
      if (manualPermissions && manualPermissions[manualId]) {
        permissionKeys = manualPermissions[manualId];
      }
      return keys.every((x) => permissionKeys.map((key) => key.toLowerCase()).includes(x.toLowerCase()));
    },
    [manualPermissions]
  );

  const manual = useMemo(() => {
    return manualQuery.data ?? ({} as DraftManual);
  }, [manualQuery.data]);

  const contextValue: DraftManualCRQProviderResponse = useMemo(
    () => ({
      manualPermissions,
      manualHasPermissions,
      setManualPermissions,
      manualId: id ?? '',
      manual,
      manualQuery,
    }),
    [manualPermissions, manualHasPermissions, setManualPermissions, id, manual, manualQuery]
  );

  return <DraftManualCRQContext.Provider value={contextValue}>{children}</DraftManualCRQContext.Provider>;
};

export default DraftManualCRQProvider;
